import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageBox, MessageBoxButtonProps } from '@client/components/MessageBox'

//
// "Cancel the conversions, or let them run?" — asked on signing out, after a
// reload and when an account is disabled (operator, 17.10.2026, F3).
//

export type TConversionsAnswer = 'cancel' | 'continue' | 'back'

type TQuestion = { message: string; withBack: boolean }

/**
 * Returns `ask`, which shows the question and resolves with the answer, and
 * the element that has to be drawn for it.
 *
 * `withBack` adds a third button that does neither — for a sign-out the user
 * may want to take back.
 */
export default function useConversionsQuestion() {
    const { t } = useTranslation()
    const [question, setQuestion] = useState<TQuestion | null>(null)
    const resolveRef = useRef<((answer: TConversionsAnswer) => void) | null>(null)

    const ask = (message: string, withBack = false) =>
        new Promise<TConversionsAnswer>((resolve) => {
            resolveRef.current = resolve
            setQuestion({ message, withBack })
        })

    const answer = (key: string) => {
        setQuestion(null)
        resolveRef.current?.(key as TConversionsAnswer)
        resolveRef.current = null
    }

    const buttons: MessageBoxButtonProps[] = [
        { key: 'cancel', text: t('Cancel conversions') },
        { key: 'continue', text: t('Let them run') },
        ...(question?.withBack ? [{ key: 'back', text: t('Back') }] : []),
    ]

    const element = question ? <MessageBox message={question.message} buttons={buttons} onClick={answer} /> : null

    return { ask, element }
}
